import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Pencil, ChevronLeft, ChevronRight, Medal } from 'lucide-react';
import { shortName } from '../../utils/playerName';
import { formatSetScore } from '../../utils/matchScore';
import type {
  CompetitionBracketData,
  CompetitionMatch,
  CompetitionTeam,
  CompetitionSlotSource,
} from '../../types';

/** Localized round name based on how many rounds remain (0 = final). */
function roundLabel(round: number, totalRounds: number): string {
  const fromEnd = totalRounds - round;
  if (fromEnd === 0) return 'Final';
  if (fromEnd === 1) return 'Semifinais';
  if (fromEnd === 2) return 'Quartas';
  if (fromEnd === 3) return 'Oitavas';
  return `Rodada ${round}`;
}

function teamLabel(team: CompetitionTeam | null): string {
  if (!team) return '—';
  return team.players.map((p) => shortName(p.name) || '?').join(' & ');
}

/** Label for an unresolved slot: a team, a placeholder source, or "A definir". */
function slotLabel(team: CompetitionTeam | null, source?: CompetitionSlotSource | null): string {
  if (team) return teamLabel(team);
  if (source) {
    switch (source.kind) {
      case 'group':
        return `${source.rank}º Grupo ${source.groupNumber}`;
      case 'winner':
        return `Vencedor do Jogo ${source.gameNumber}`;
      case 'loser':
        return `Perdedor do Jogo ${source.gameNumber}`;
      case 'bye':
        return 'BYE';
    }
  }
  return 'A definir';
}

interface BracketViewProps {
  data: CompetitionBracketData;
  isAdmin: boolean;
  onEnterResult: (match: CompetitionMatch) => void;
}

/**
 * Knockout bracket shown ONE phase at a time, navigated with chevron buttons.
 * The last phase ("Final") also surfaces the third-place playoff when present.
 */
export function BracketView({ data, isAdmin, onEnterResult }: BracketViewProps) {
  const totalRounds = data.rounds.length;

  // Default to the earliest phase with a playable pending match; if the whole
  // bracket is decided, land on the final.
  const initialPhase = useMemo(() => {
    for (let i = 0; i < data.rounds.length; i++) {
      if (data.rounds[i].matches.some((m) => m.status === 'pending' && m.team1 && m.team2)) return i;
    }
    const allDone = data.rounds.every((r) => r.matches.every((m) => m.status === 'completed'));
    return allDone ? Math.max(0, data.rounds.length - 1) : 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalRounds]);

  const [phase, setPhase] = useState(initialPhase);
  const [dir, setDir] = useState(0);

  if (totalRounds === 0) {
    return (
      <p className="text-center text-white/40 text-sm py-8">
        Este torneio não tem fase de mata-mata.
      </p>
    );
  }

  const clampedPhase = Math.min(phase, totalRounds - 1);
  const round = data.rounds[clampedPhase];
  const go = (delta: number) => {
    const next = Math.min(totalRounds - 1, Math.max(0, clampedPhase + delta));
    if (next !== clampedPhase) {
      setDir(delta);
      setPhase(next);
    }
  };

  // Within a round, the third-place match renders separately, after the rest.
  const mainMatches = round.matches.filter((m) => !m.isThirdPlace);
  const thirdPlace = round.matches.find((m) => m.isThirdPlace);

  return (
    <div className="space-y-4">
      {/* The champion banner is rendered once at the page level — kept out of
          here to avoid duplication; the dedicated Pódio tab shows the full podium. */}

      {/* Phase navigator */}
      <div className="flex items-center justify-between gap-2">
        <button
          onClick={() => go(-1)}
          disabled={clampedPhase === 0}
          className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-25 disabled:hover:bg-transparent"
          aria-label="Fase anterior"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 text-center">
          <p className="text-sm font-semibold text-white uppercase tracking-wide">
            {roundLabel(round.round, totalRounds)}
          </p>
          <div className="flex items-center justify-center gap-1 mt-1.5">
            {data.rounds.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === clampedPhase ? 'w-4 bg-turquoise' : 'w-1.5 bg-white/20'
                }`}
              />
            ))}
          </div>
        </div>
        <button
          onClick={() => go(1)}
          disabled={clampedPhase === totalRounds - 1}
          className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-25 disabled:hover:bg-transparent"
          aria-label="Próxima fase"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Sliding phase content */}
      <div className="relative overflow-hidden">
        <AnimatePresence mode="wait" custom={dir} initial={false}>
          <motion.div
            key={clampedPhase}
            custom={dir}
            initial={{ opacity: 0, x: dir >= 0 ? 40 : -40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: dir >= 0 ? -40 : 40 }}
            transition={{ duration: 0.2 }}
            className="space-y-3"
          >
            {mainMatches.map((match) => (
              <BracketMatchCard
                key={match.id}
                match={match}
                isAdmin={isAdmin}
                onEnterResult={onEnterResult}
              />
            ))}

            {thirdPlace && (
              <div className="pt-2">
                <p className="text-[11px] font-semibold text-white/40 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                  <Medal className="w-3.5 h-3.5" />
                  Disputa de 3º lugar
                </p>
                <BracketMatchCard
                  match={thirdPlace}
                  isAdmin={isAdmin}
                  onEnterResult={onEnterResult}
                />
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function BracketMatchCard({
  match,
  isAdmin,
  onEnterResult,
}: {
  match: CompetitionMatch;
  isAdmin: boolean;
  onEnterResult: (match: CompetitionMatch) => void;
}) {
  const bothDefined = !!match.team1 && !!match.team2;
  const canEnter = isAdmin && bothDefined;
  const done = match.status === 'completed';

  return (
    <div className="glass-card rounded-xl p-2.5">
      {match.gameNumber != null && (
        <p className="text-[10px] font-medium text-white/30 uppercase tracking-wide mb-1">
          Jogo {match.gameNumber}
        </p>
      )}

      <TeamRow
        team={match.team1}
        source={match.team1Source}
        games={match.team1Games}
        oppGames={match.team2Games}
        tiebreakScore={match.tiebreakScore}
        isWinner={match.winnerTeamId != null && match.winnerTeamId === match.team1?.id}
        isWo={match.isWo}
      />
      <div className="h-px bg-white/10 my-1.5" />
      <TeamRow
        team={match.team2}
        source={match.team2Source}
        games={match.team2Games}
        oppGames={match.team1Games}
        tiebreakScore={match.tiebreakScore}
        isWinner={match.winnerTeamId != null && match.winnerTeamId === match.team2?.id}
        isWo={match.isWo}
      />

      {canEnter && (
        <button
          onClick={() => onEnterResult(match)}
          className="mt-2 w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium text-turquoise hover:bg-turquoise/10 transition-colors"
        >
          <Pencil className="w-3.5 h-3.5" />
          {done ? 'Editar resultado' : 'Registrar resultado'}
        </button>
      )}
    </div>
  );
}

function TeamRow({
  team,
  source,
  games,
  oppGames,
  tiebreakScore,
  isWinner,
  isWo,
}: {
  team: CompetitionTeam | null;
  source?: CompetitionSlotSource | null;
  games: number | null;
  oppGames: number | null;
  tiebreakScore: number | null;
  isWinner: boolean;
  isWo: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span
        className={`text-sm min-w-0 ${
          // `truncate` (overflow:hidden) clips the right slant of italic glyphs,
          // so only truncate resolved team names; placeholders are short + italic.
          team ? 'truncate' : 'italic text-white/30'
        } ${isWinner ? 'text-white font-semibold' : 'text-white/60'}`}
      >
        {slotLabel(team, source)}
      </span>
      <span className={`text-sm shrink-0 ${isWinner ? 'text-turquoise font-bold' : 'text-white/40'}`}>
        {isWo ? (isWinner ? 'W.O.' : '—') : formatSetScore(games, oppGames, tiebreakScore)}
      </span>
    </div>
  );
}
